import api from "@/api";
import router from "@/router";
import i18n from "@/i18n";
import { createLatestRequestGate, teamContextRequiredError, teamScopedCacheKey } from "@/helpers/teamAuthorization";

const { t } = i18n.global;
const reportRequestGate = createLatestRequestGate();

export default {
  state: {
    reports: [],
  },
  getters: {
    reports: (state) => state.reports,
  },
  mutations: {
    setReports: (state, data) => {
      state.reports = data;
    },
  },
  actions: {
    getReports: async ({ commit, dispatch, rootGetters }, request = {}) => {
      try {
        const normalizedRequest =
          request && typeof request === "object"
            ? request
            : { team_id: request };
        const query = normalizedRequest.query || router.currentRoute?.value?.query || {};
        const team_id = normalizedRequest.team_id || normalizedRequest.teamId || null;
        const {
          userId,
          teamId,
          paidStatus,
          month,
          year,
          ahv,
          bvg,
          bvgValue,
          startDate,
          endDate,
        } = query;
        const requestedTeamId = Number(team_id || teamId);
        if (!Number.isInteger(requestedTeamId) || requestedTeamId <= 0) {
          throw teamContextRequiredError();
        }
        const requestKey = teamScopedCacheKey({
          endpoint: "/v1/reports",
          teamId: requestedTeamId,
          query,
          actorId: rootGetters.loggedUser?.id,
        });
        const { signal } = reportRequestGate.start(requestKey);

        let requestPayload = {
          paid_status: paidStatus === "true",
          user_id: userId,
          month: month,
          year: year,
          ahv: ahv === "true",
          bvg: bvg === "true",
          team_id: requestedTeamId,
          bvg_value: bvg === "true" ? bvgValue : 0,
        };

        if (startDate && endDate) {
          requestPayload = {
            ...requestPayload,
            start_date: startDate,
            end_date: endDate,
          };
        }

        const {
          data: { data },
        } = await api({ requiresAuth: true }).post("/v1/reports", requestPayload, { signal });

        if (!reportRequestGate.isActive(requestKey)) return;
        commit(
          "setReports",
          data.map((period) => {
            for (let p in period) {
              return { ...period[p], period: p };
            }
          })
        );
      } catch (err) {
        if (err.code === "ERR_CANCELED") return;
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          t("messages.somethingWentWrong");

        dispatch("showToast", {
          type: "error",
          message,
        }); 
      }
    },

    getPaymentReport: async ({ commit, dispatch }, request = {}) => {
      try {
        const normalizedRequest =
          request && typeof request === "object"
            ? request
            : {};
        const query = normalizedRequest.query || router.currentRoute?.value?.query || {};
        const {
          userId,
          paidStatus,
          month,
          year,
          ahv,
          bvg,
          bvgValue,
          startDate,
          endDate,
        } = query;

        let requestPayload = {
          paid_status: paidStatus === "true",
          user_id: userId,
          month: month,
          year: year,
          ahv: ahv === "true",
          bvg: bvg === "true",
          bvg_value: bvg === "true" ? bvgValue : 0,
        };

        if (startDate && endDate) {
          requestPayload = {
            ...requestPayload,
            start_date: startDate,
            end_date: endDate,
          };
        }

        const {
          data: { data },
        } = await api({ requiresAuth: true }).post("/v1/payment-report", {
          ...requestPayload,
        });

        commit(
          "setReports",
          data.map((period) => {
            for (let p in period) {
              return { ...period[p], period: p };
            }
          })
        );
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          t("messages.somethingWentWrong");

        dispatch("showToast", {
          type: "error",
          message,
        }); 
      }
    },

    makePayment: async ({ dispatch }, data) => {
      try {
        return await api({ requiresAuth: true }).patch("/v1/reports/payments", {
          ...data,
        });
      } catch (err) {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          t("messages.somethingWentWrong");

        dispatch("showToast", {
          type: "error",
          message,
        });

        throw err;
      }
    },
  },
};
